// Shared helpers for the unified timed-screen layout — the per-phase chip
// label and the Outstanding-panel item builder. Extracted so both
// `PhasePane` (single-phase screens) and `PrepPane` (the split, which
// hoists ONE shared Outstanding panel above its two task columns) build
// the list from one code path.

import type { ClassifiedPlan, ScheduleEntry } from '@cookchow/shared';
import type { OutstandingItem } from '../design/components';
import { formatHHMM, formatOverdue } from './helpers';

export type PhaseChipLabel = 'PreCook' | 'PrepCook' | 'LiveCook';

export function phaseChipFor(phase: ScheduleEntry['phase']): PhaseChipLabel {
  if (phase === 'preCook') return 'PreCook';
  if (phase === 'prepCook') return 'PrepCook';
  return 'LiveCook';
}

// Overrun tasks, ordered by end time (the order they timed out), mapped to
// the OutstandingPanel item shape. `nowMs` drives the "overdue by" label so
// it recomputes each tick.
export function buildOutstandingItems(
  tasks: readonly ScheduleEntry[],
  classified: ClassifiedPlan | null,
  nowMs: number,
): OutstandingItem[] {
  if (!classified) return [];
  const overrunIds = new Set(
    classified.tasks.filter((ct) => ct.state === 'overrun').map((ct) => ct.task.id),
  );
  return tasks
    .filter((t) => overrunIds.has(t.id))
    .slice()
    .sort((a, b) => Date.parse(a.mustCompleteBy) - Date.parse(b.mustCompleteBy))
    .map((task) => ({
      id: task.id,
      label: task.action,
      phaseChip: phaseChipFor(task.phase),
      time: formatHHMM(task.startAt),
      endTime: formatHHMM(task.mustCompleteBy),
      overdueLabel: formatOverdue((nowMs - Date.parse(task.mustCompleteBy)) / 1000),
    }));
}

// Phase bounds in epoch ms: earliest task start → latest task deadline.
// Used to anchor a phase's timeline row and to size the prep top-bar timer.
export function phaseBounds(tasks: readonly ScheduleEntry[]): {
  startMs: number;
  finishMs: number;
} {
  let startMs = Number.POSITIVE_INFINITY;
  let finishMs = Number.NEGATIVE_INFINITY;
  for (const t of tasks) {
    const s = Date.parse(t.startAt);
    const f = Date.parse(t.mustCompleteBy);
    if (Number.isFinite(s) && s < startMs) startMs = s;
    if (Number.isFinite(f) && f > finishMs) finishMs = f;
  }
  return {
    startMs: Number.isFinite(startMs) ? startMs : 0,
    finishMs: Number.isFinite(finishMs) ? finishMs : 0,
  };
}

// A phase "has timed tasks" when at least one task carries a real
// start/stop window (`mustCompleteBy` strictly after `startAt`) — i.e. a
// task that can run, and therefore overrun, against the clock. A phase
// whose tasks have no such window (a flat checklist) gets no Outstanding
// panel: there is nothing to time out.
export function phaseHasTimedTasks(tasks: readonly ScheduleEntry[]): boolean {
  return tasks.some((t) => Date.parse(t.mustCompleteBy) > Date.parse(t.startAt));
}
